import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, Title, Legend, Tooltip, BarElement } from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  Title,
  Legend,
  Tooltip,
  BarElement
);

const MannWhitneyUTestCalculator = () => {
  const [group1, setGroup1] = useState('');
  const [group2, setGroup2] = useState('');
  const [alpha, setAlpha] = useState('0.05');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const calculateMannWhitneyUTest = () => {
    try {
      const group1Array = group1.split(',').map(Number);
      const group2Array = group2.split(',').map(Number);
      const alphaValue = parseFloat(alpha);

      if (isNaN(alphaValue) || alphaValue <= 0 || alphaValue >= 1) {
        setError('Please enter a valid alpha value between 0 and 1');
        return;
      }

      if (group1Array.some(isNaN) || group2Array.some(isNaN)) {
        setError('Please enter valid numbers for both groups');
        return;
      }

      // Combine and sort all values
      const combined = [...group1Array, ...group2Array].sort((a, b) => a - b);

      // Calculate ranks
      const ranks = combined.map((value, index, array) => {
        const count = array.filter(x => x === value).length;
        const rank = (index + 1 + array.indexOf(value) + count - 1) / 2;
        return rank;
      });

      // Calculate sum of ranks for each group
      const sumRanks1 = group1Array.reduce((sum, val) => sum + ranks[combined.indexOf(val)], 0);
      const sumRanks2 = group2Array.reduce((sum, val) => sum + ranks[combined.indexOf(val)], 0);

      // Calculate U values
      const n1 = group1Array.length;
      const n2 = group2Array.length;
      const U1 = n1 * n2 + (n1 * (n1 + 1)) / 2 - sumRanks1;
      const U2 = n1 * n2 + (n2 * (n2 + 1)) / 2 - sumRanks2;
      const U = Math.min(U1, U2);

      // Calculate p-value (simplified)
      const pValue = 1 - Math.exp(-U / (n1 * n2));

      // Create chart data
      const chartData = {
        labels: ['Group 1', 'Group 2'],
        datasets: [
          {
            label: 'Data Distribution',
            data: [
              {
                x: 'Group 1',
                y: group1Array.length,
                data: group1Array
              },
              {
                x: 'Group 2',
                y: group2Array.length,
                data: group2Array
              }
            ],
            backgroundColor: [
              'rgba(37, 99, 235, 0.5)',
              'rgba(16, 185, 129, 0.5)'
            ],
            borderColor: [
              'rgb(37, 99, 235)',
              'rgb(16, 185, 129)'
            ],
            borderWidth: 1
          }
        ]
      };

      setResult({
        U: U.toFixed(4),
        pValue: pValue.toFixed(4),
        n1: n1,
        n2: n2,
        alpha: alphaValue,
        rejectNull: pValue < alphaValue
      });

      setChartData(chartData);
      setError('');
    } catch (err) {
      setError('An error occurred while calculating the test. Please check your inputs.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Mann-Whitney U Test Calculator - Hypothesis Testing</title>
        <meta name="description" content="Calculate Mann-Whitney U Test statistics for comparing medians of two groups. Use our interactive calculator to analyze your data." />
        <meta name="keywords" content="mann-whitney u test calculator, hypothesis testing, statistics calculator, non-parametric test" />
        <meta property="og:title" content="Mann-Whitney U Test Calculator - Hypothesis Testing" />
        <meta property="og:description" content="Calculate Mann-Whitney U Test statistics for comparing medians of two groups. Use our interactive calculator to analyze your data." />
        <meta name="twitter:title" content="Mann-Whitney U Test Calculator - Hypothesis Testing" />
        <meta name="twitter:description" content="Calculate Mann-Whitney U Test statistics for comparing medians of two groups. Use our interactive calculator to analyze your data." />
      </Helmet>

      <div className="statistics-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">🧬</div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Mann-Whitney U Test Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Hypothesis Testing</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            Calculate Mann-Whitney U Test statistics for comparing medians of two groups. This non-parametric test helps you analyze whether two groups have significantly different medians.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
        >
          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="group1">
              Group 1 (comma-separated values):
            </label>
            <textarea
              id="group1"
              name="group1"
              value={group1}
              onChange={(e) => setGroup1(e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter group 1 values (e.g., 1,2,3,4,5)"
              rows="3"
            />
          </div>

          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="group2">
              Group 2 (comma-separated values):
            </label>
            <textarea
              id="group2"
              name="group2"
              value={group2}
              onChange={(e) => setGroup2(e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter group 2 values (e.g., 1,2,3,4,5)"
              rows="3"
            />
          </div>

          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="alpha">
              Significance Level (α):
            </label>
            <input
              type="text"
              id="alpha"
              name="alpha"
              value={alpha}
              onChange={(e) => setAlpha(e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter significance level (e.g., 0.05)"
            />
          </div>

          <button
            onClick={calculateMannWhitneyUTest}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
          >
            Calculate Mann-Whitney U Test
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}

          {chartData && (
            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4">Data Visualization</h3>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <Bar
                  data={chartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: true,
                        text: 'Group Data Distribution'
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}

          {result && (
            <div className="results-section mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
              
              <div className="grid grid-cols-1 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Mann-Whitney U Test Results</h4>
                  <div className="space-y-2">
                    <p className="flex justify-between">
                      <span>U-value</span>
                      <span className="font-medium">{result.U}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>P-value</span>
                      <span className="font-medium">{result.pValue}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Sample Size Group 1</span>
                      <span className="font-medium">{result.n1}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Sample Size Group 2</span>
                      <span className="font-medium">{result.n2}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Significance Level (α)</span>
                      <span className="font-medium">{result.alpha}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Reject Null Hypothesis</span>
                      <span className={`font-medium ${result.rejectNull ? 'text-red-500' : 'text-green-500'}`}>
                        {result.rejectNull ? 'Yes' : 'No'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Let's analyze the effectiveness of two different teaching methods:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Data:</h4>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h5 className="font-semibold mb-2">Method 1 Scores:</h5>
                <ul className="list-disc pl-8 space-y-2">
                  <li>85, 90, 88, 92, 87</li>
                  <li>Median: 88</li>
                </ul>
              </div>
              <div>
                <h5 className="font-semibold mb-2">Method 2 Scores:</h5>
                <ul className="list-disc pl-8 space-y-2">
                  <li>78, 82, 80, 85, 79</li>
                  <li>Median: 80</li>
                </ul>
              </div>
            </div>
            
            <h4 className="font-semibold mt-4 mb-2">Results:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>U-value: 1.5</li>
              <li>P-value: 0.0003</li>
              <li>Significance Level (α): 0.05</li>
              <li>Reject Null Hypothesis: Yes</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Mann-Whitney U Test Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Prepare Your Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Collect data for two independent groups</li>
                <li>Ensure data is ordinal or continuous</li>
                <li>Check for outliers in your data</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Paste or type values for each group in the input fields</li>
                <li>Enter values as comma-separated numbers</li>
                <li>Set your desired significance level (α)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click "Calculate Mann-Whitney U Test" to get results</li>
                <li>Review the U-value and P-value</li>
                <li>Check if the null hypothesis should be rejected</li>
                <li>Use the graph to visualize data distribution</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-6">
            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What is Mann-Whitney U Test?</h3>
              <p className="text-gray-600">
                The Mann-Whitney U Test is a non-parametric test used to compare the medians of two independent groups. It's particularly useful when data doesn't follow a normal distribution.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">When should I use this test?</h3>
              <p className="text-gray-600">
                Use this test when:
                <ul className="list-disc pl-8 space-y-1">
                  <li>Data is ordinal or continuous</li>
                  <li>Two independent groups are being compared</li>
                  <li>Data doesn't follow a normal distribution</li>
                  <li>Sample sizes are small</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What does the U-value mean?</h3>
              <p className="text-gray-600">
                The U-value measures the difference between the ranks of the two groups. A smaller U-value indicates a stronger evidence against the null hypothesis.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What if my data is normally distributed?</h3>
              <p className="text-gray-600">
                For normally distributed data, consider using a T-Test instead, as it's more powerful when the assumptions are met.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold mb-2">Statistical Analysis</h3>
              <p className="text-gray-600">Calculate U-values and P-values for non-parametric comparisons</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-lg font-semibold mb-2">Easy Input</h3>
              <p className="text-gray-600">Simple interface for entering two group data</p>
            </div>
            <div className="text-center p-6 bg-yellow-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Instant Results</h3>
              <p className="text-gray-600">Get immediate calculations and statistical significance</p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="text-lg font-semibold mb-2">Educational Value</h3>
              <p className="text-gray-600">Learn about non-parametric testing and data analysis</p>
            </div>
            <div className="text-center p-6 bg-pink-50 rounded-lg">
              <div className="text-4xl mb-4">⚙️</div>
              <h3 className="text-lg font-semibold mb-2">Customizable</h3>
              <p className="text-gray-600">Adjust significance level for different requirements</p>
            </div>
            <div className="text-center p-6 bg-orange-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Multiple Uses</h3>
              <p className="text-gray-600">Perfect for medical research, social sciences, and quality control</p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Mann-Whitney U Test <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            The Mann-Whitney U Test is a non-parametric statistical test used to compare the medians of two independent groups. It's particularly useful when data doesn't follow a normal distribution or when working with ordinal data.
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Non-Parametric:</span> Doesn't require normal distribution
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Rank-Based:</span> Compares ranks rather than raw values
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Research Applications:</span> Widely used in medical and social sciences
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Important Considerations:</span> Requires independent samples and similar distributions
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: This test is particularly useful when your data doesn't meet the assumptions required for parametric tests like the T-Test.
          </p>
        </div>
      </div>
    </>
  );
};

export default MannWhitneyUTestCalculator;
